import { useState } from "react";
import { deDatum, fmtEuro, jahrLabel } from "../kern/format.js";
import { FLASCHENGROESSEN } from "../kern/konstanten.js";
import { eintragName, unterschiedeKellerbuch } from "../kern/dreiwege.js";
import { standIstLeer, standKennzahlen, vergleicheStaende } from "../kern/sync.js";
import { Btn, TextKnopf } from "../ui/primitive.jsx";
import { t } from "../i18n/index.js";


/* ═══════════════════════════════════════════════════════════════
   Dialog „Daten synchronisieren"

   Vier ruhige Ausgänge (aktuell, hochgeladen, heruntergeladen,
   offline) und ein fünfter, der eine Entscheidung verlangt: beide
   Seiten wurden seit dem letzten Abgleich geändert.

   Die Gegenüberstellung zeigt Kennzahlen statt Zeitstempeln, weil
   ein Zeitstempel im Konfliktfall wenig hilft — beide Stände sind
   „neu". Woran ein Nutzer seinen Stand erkennt, ist die Zahl der
   Flaschen und die letzte Bewegung.
   ═══════════════════════════════════════════════════════════════ */


/* Eine Kennzahlzeile der Gegenüberstellung. */
function Zeile({ label, lokal, server, abweichung, num = true }) {
  return (
    <div className={"wl-syncvgl-zeile" + (abweichung ? " ist-anders" : "")}>
      <span className="wl-syncvgl-name">{t(label)}</span>
      <span className={"wl-syncvgl-wert" + (num ? " num" : "")}>{lokal}</span>
      <span className={"wl-syncvgl-wert" + (num ? " num" : "")}>{server}</span>
    </div>
  );
}


/* ── Einzelheiten ────────────────────────────────────────────────
   Woran sich die beiden Stände unterscheiden, in Worten, die ein
   Nutzer wiedererkennt: „Scharzhofberger 2015 · Lagerort: hier B2,
   Server C1“ statt einer Feldkennung. */

const SAMMLUNG_LABEL = {
  regions: "Region", winzer: "Winzer", weine: "Wein", kritiken: "Kritik",
  entnahmen: "Entnahme", bewegungen: "Bewegung", weinkarten: "Weinkarte",
  prefs: "Einstellung", katalogAbweichungen: "Katalogabweichung",
};

const FELD_LABEL = {
  name: "Name", ort: "Ort", land: "Land", beschreibung: "Beschreibung", regionId: "Region",
  winzerId: "Winzer", typ: "Typ", appellation: "Appellation", klassifikation: "Klassifikation",
  boden: "Boden", label: "Label", favorit: "Favorit", rebsorten: "Rebsorten", rebsortenListe: "Rebsorten",
  lagerort: "Lagerort", preis: "Preis", trinkVon: "Trinkreife ab", trinkBis: "Trinkreife bis",
  jahr: "Jahr", foto: "Etikett", rundum: "Rundum-Etikett", wert: "Bewertung", text: "Notiz", menge: "Menge", datum: "Datum",
  alkohol: "Alkohol", saeure: "Säure", restzucker: "Restzucker", ph: "pH", schwefel: "Schwefel",
  nurBestand: "Nur mit Bestand",
};

const groesseKurz = (key) => (FLASCHENGROESSEN.find((g) => g.key === key) || {}).kurz || `${key} l`;

/* „jahrgaenge[j15].bestaende.0.75“ → { jgId: "j15", feld: "bestaende", groesse: "0.75" } */
function zerlegePfad(pfad) {
  if (!pfad) return {};
  const m = /^jahrgaenge\[([^\]]+)\]\.?(.*)$/.exec(pfad);
  if (m) {
    const rest = m[2] || "";
    if (rest.startsWith("bestaende.")) return { jgId: m[1], feld: "bestaende", groesse: rest.slice(10) };
    return { jgId: m[1], feld: rest || null };
  }
  const e = /^eintraege\[([^\]]+)\]\.?(.*)$/.exec(pfad);
  if (e) return { eintragId: e[1], feld: e[2] || null };
  return { feld: pfad.split(".").pop() };
}

function zeigeWert(feld, wert) {
  if (wert === null || wert === undefined || wert === "") return "—";
  if (feld === "preis" && typeof wert === "number") return fmtEuro(wert);
  if (typeof wert === "boolean") return wert ? t("ja") : t("nein");
  if (typeof wert === "object") return t("vorhanden");
  const text = String(wert);
  return text.length > 60 ? text.slice(0, 59) + "…" : text;
}

/** Eine Zeile je Widerspruch. */
function konfliktZeile(k, lokalDb, serverDb) {
  const quelle = (st) => ((st || {})[k.sammlung] || []).find((o) => o.id === k.id || o.eintragId === k.id);
  const obj = quelle(lokalDb) || quelle(serverDb);
  const teil = zerlegePfad(k.feld);
  let titel = `${t(SAMMLUNG_LABEL[k.sammlung] || k.sammlung)} ${eintragName(k.sammlung, obj, lokalDb, serverDb)}`.trim();
  if (teil.jgId && obj && obj.jahrgaenge) {
    const jg = (obj.jahrgaenge || []).find((j) => j.id === teil.jgId)
      || (((quelle(serverDb) || {}).jahrgaenge) || []).find((j) => j.id === teil.jgId);
    if (jg) titel += ` ${jahrLabel(jg.jahr)}`;
  }
  if (k.art === "geloescht") {
    return {
      titel, feld: null,
      hier: k.lokal ? t("geändert") : t("gelöscht"),
      server: k.server ? t("geändert") : t("gelöscht"),
    };
  }
  const feld = teil.feld === "bestaende"
    ? t("Bestand {g}", { g: groesseKurz(teil.groesse) })
    : t(FELD_LABEL[teil.feld] || teil.feld || "Wert");
  return { titel, feld, hier: zeigeWert(teil.feld, k.lokal), server: zeigeWert(teil.feld, k.server) };
}

function Widersprueche({ konflikte, db, serverDb }) {
  const zeilen = konflikte.slice(0, 40).map((k) => konfliktZeile(k, db, serverDb));
  return (
    <div className="wl-syncdetail">
      <div className="wl-syncdetail-kopf">
        <span />
        <span>{t("Dieses Gerät")}</span>
        <span>{t("Server")}</span>
      </div>
      {zeilen.map((z, i) => (
        <div key={i} className="wl-syncdetail-zeile">
          <span className="wl-syncdetail-was">
            <span className="wl-syncdetail-titel">{z.titel}</span>
            {z.feld && <span className="wl-syncdetail-feld">{z.feld}</span>}
          </span>
          <span className="wl-syncdetail-wert">{z.hier}</span>
          <span className="wl-syncdetail-wert">{z.server}</span>
        </div>
      ))}
      {konflikte.length > 40 && (
        <p className="wl-statleer">{t("… und {n} weitere", { n: konflikte.length - 40 })}</p>
      )}
    </div>
  );
}

/* Ohne Basis: welche Einträge gibt es nur hier, nur dort, oder
   verschieden — damit die Wahl nicht blind geschieht. */
function Unterschiede({ db, serverDb }) {
  const liste = unterschiedeKellerbuch(db, serverDb)
    .filter((u) => !["entnahmen"].includes(u.sammlung));     /* doppeln die Bewegungen */
  if (liste.length === 0) return null;
  const art = { nurHier: t("nur hier"), nurServer: t("nur auf dem Server"), verschieden: t("verschieden") };
  return (
    <div className="wl-syncdetail">
      {liste.slice(0, 30).map((u) => (
        <div key={u.sammlung + u.id} className="wl-syncdetail-zeile wl-syncdetail-zeile--zwei">
          <span className="wl-syncdetail-was">
            <span className="wl-syncdetail-titel">
              {t(SAMMLUNG_LABEL[u.sammlung] || u.sammlung)} {eintragName(u.sammlung, u.lokal || u.server, db, serverDb)}
            </span>
          </span>
          <span className="wl-syncdetail-wert">{art[u.art]}</span>
        </div>
      ))}
      {liste.length > 30 && <p className="wl-statleer">{t("… und {n} weitere", { n: liste.length - 30 })}</p>}
    </div>
  );
}


function Konflikt({ db, serverDb, serverVersion, ergebnis, onLokal, onServer, onZusammenfuehren, onClose }) {
  const [laeuft, setLaeuft] = useState(null); /* "lokal" | "server" | "zus-lokal" | "zus-server" */
  const [ganz, setGanz] = useState(false);     /* einen Stand ganz übernehmen statt zusammenführen */

  const waehle = async (welche, fn) => {
    if (laeuft) return;
    setLaeuft(welche);
    try { await fn(); } finally { setLaeuft(null); }
  };

  /* Mit Basis: zusammenführen, nur die Widersprüche stehen zur Wahl. */
  if (ergebnis && !ganz) {
    const n = ergebnis.konflikte.length;
    return (
      <>
        <p className="wl-fliesstext">
          {t("Auf diesem Gerät und auf dem Server wurde seit dem letzten Abgleich jeweils etwas geändert. Alles, was sich nicht widerspricht, wird zusammengeführt. An diesen Stellen haben beide Seiten dasselbe verschieden geändert:")}
        </p>
        <Widersprueche konflikte={ergebnis.konflikte} db={db} serverDb={serverDb} />
        <p className="wl-statleer">
          {t.plural(n, "Ein Widerspruch · Serverstand {v}", "{n} Widersprüche · Serverstand {v}", { v: serverVersion })}
        </p>
        <div className="wl-modalfuss hat-links wl-syncfuss">
          <Btn variante="ghost" onClick={onClose} disabled={!!laeuft}>{t("Später entscheiden")}</Btn>
          <div className="wl-modalfuss-rechts">
            <Btn variante="linie" onClick={() => waehle("zus-server", () => onZusammenfuehren("server"))} disabled={!!laeuft}>
              {laeuft === "zus-server" ? t("Einen Moment …") : t("Zusammenführen, Server gilt")}
            </Btn>
            <Btn variante="voll" onClick={() => waehle("zus-lokal", () => onZusammenfuehren("lokal"))} disabled={!!laeuft}>
              {laeuft === "zus-lokal" ? t("Einen Moment …") : t("Zusammenführen, dieses Gerät gilt")}
            </Btn>
          </div>
        </div>
        <p className="wl-statleer">
          <TextKnopf className="ist-dezent" onClick={() => setGanz(true)} disabled={!!laeuft}>
            {t("Stattdessen einen Stand ganz übernehmen")}
          </TextKnopf>
        </p>
      </>
    );
  }

  return <GanzerStand db={db} serverDb={serverDb} serverVersion={serverVersion} laeuft={laeuft}
    zurueck={ergebnis ? () => setGanz(false) : null}
    onLokal={() => waehle("lokal", onLokal)} onServer={() => waehle("server", onServer)} onClose={onClose} />;
}


/* Die bisherige Wahl: ein Stand gewinnt ganz. Bleibt für den Fall
   ohne Basis und als bewusster Ausweg. */
function GanzerStand({ db, serverDb, serverVersion, laeuft, zurueck, onLokal, onServer, onClose }) {
  const zeilen = vergleicheStaende(db, serverDb);
  const lokalLeer = standIstLeer(db);
  const serverLeer = standIstLeer(serverDb);

  /* Das Datum der letzten Bewegung steht nicht in der Kennzahlliste,
     ist aber der stärkste Wiedererkennungswert im Konfliktfall. */
  const letzte = (stand) => {
    const d = standKennzahlen(stand).letzteBewegung;
    return d ? deDatum(String(d).slice(0, 10)) : "—";
  };
  const letzteLokal = letzte(db);
  const letzteServer = letzte(serverDb);

  return (
    <>
      <p className="wl-fliesstext">
        {zurueck
          ? t("Ein Stand wird ganz übernommen, der andere überschrieben — auch dort, wo sich nichts widerspricht.")
          : t("Auf diesem Gerät und auf dem Server wurde seit dem letzten Abgleich jeweils etwas geändert. Ohne gemeinsamen Ausgangsstand lassen sich beide nicht sicher zusammenführen — bitte einen davon auswählen. Der andere wird überschrieben.")}
      </p>

      <div className="wl-syncvgl">
        <div className="wl-syncvgl-kopf">
          <span className="wl-syncvgl-name" />
          <span className="wl-syncvgl-wert">{t("Dieses Gerät")}</span>
          <span className="wl-syncvgl-wert">{t("Server")}</span>
        </div>
        {zeilen.map((z) => (
          <Zeile key={z.schluessel} label={z.label} lokal={z.lokal} server={z.server} abweichung={z.abweichung} />
        ))}
        <Zeile label="Letzte Bewegung" lokal={letzteLokal} server={letzteServer}
          abweichung={letzteLokal !== letzteServer} num={false} />
      </div>

      <p className="wl-statleer">
        {t("Serverstand {v}", { v: serverVersion })}
      </p>

      {!zurueck && <Unterschiede db={db} serverDb={serverDb} />}

      {/* Nur wenn genau eine Seite leer ist. Sind es beide, gibt es
          nichts zu verlieren und die Warnung wäre nur Lärm. */}
      {(lokalLeer !== serverLeer) && (
        <p className="wl-syncwarnung">
          {lokalLeer
            ? t("Achtung: Dieses Gerät hat kein gefülltes Kellerbuch. „Dieses Gerät behalten“ würde den Serverstand leeren.")
            : t("Achtung: Auf dem Server liegt kein gefülltes Kellerbuch. „Server behalten“ würde den hiesigen Stand verwerfen.")}
        </p>
      )}

      <div className="wl-modalfuss hat-links wl-syncfuss">
        <Btn variante="ghost" onClick={zurueck || onClose} disabled={!!laeuft}>
          {zurueck ? t("Zurück") : t("Später entscheiden")}
        </Btn>
        <div className="wl-modalfuss-rechts">
          <Btn variante="linie" onClick={onServer} disabled={!!laeuft}>
            {laeuft === "server" ? t("Einen Moment …") : t("Server behalten")}
          </Btn>
          <Btn variante="voll" onClick={onLokal} disabled={!!laeuft}>
            {laeuft === "lokal" ? t("Einen Moment …") : t("Dieses Gerät behalten")}
          </Btn>
        </div>
      </div>
    </>
  );
}


/* Der Katalogabgleich in einer Zeile: was angekommen ist und was
   eine Entscheidung verlangt. Bewusst ohne Einzelheiten — die stehen
   unter Katalog → Änderungen bzw. Meine Vorschläge. */
function KatalogZeile({ bericht }) {
  if (bericht === "laeuft") return <p className="wl-statleer">{t("Katalog wird abgeglichen …")}</p>;
  if (bericht.fehler) return <p className="wl-statleer">{t("Der Katalog war nicht erreichbar; das holt der nächste Abgleich nach.")}</p>;
  const teile = [
    bericht.eingespielt > 0 ? t.plural(bericht.eingespielt, "Eine Katalogänderung übernommen.", "{n} Katalogänderungen übernommen.") : null,
    bericht.zusammengefuehrt > 0 ? t.plural(bericht.zusammengefuehrt, "Ein Eintrag im Katalog zusammengeführt.", "{n} Einträge im Katalog zusammengeführt.") : null,
    bericht.abweichend > 0 ? t.plural(bericht.abweichend, "Eine Katalogänderung weicht von deiner Fassung ab — siehe Katalog → Änderungen.", "{n} Katalogänderungen weichen von deiner Fassung ab — siehe Katalog → Änderungen.") : null,
    bericht.angenommen > 0 ? t.plural(bericht.angenommen, "Ein Vorschlag wurde angenommen.", "{n} Vorschläge wurden angenommen.") : null,
    bericht.abgelehnt > 0 ? t.plural(bericht.abgelehnt, "Ein Vorschlag wurde abgelehnt.", "{n} Vorschläge wurden abgelehnt.") : null,
    bericht.gesendet > 0 ? t.plural(bericht.gesendet, "Ein neuer Vorschlag ging an den Verwalter.", "{n} neue Vorschläge gingen an den Verwalter.") : null,
    bericht.selbst > 0 ? t.plural(bericht.selbst, "Eine eigene Änderung ging direkt in den Katalog.", "{n} eigene Änderungen gingen direkt in den Katalog.") : null,
    bericht.luecke ? t("Dieses Gerät hat lange nicht abgeglichen — ein Teil des Katalogprotokolls liegt nicht mehr vor. Was von der Katalogfassung abweicht, steht zur Entscheidung unter Katalog → Änderungen, statt als Vorschlag gesendet zu werden.") : null,
  ].filter(Boolean);
  return <p className="wl-statleer">{teile.length ? teile.join(" ") : t("Katalog: nichts Neues.")}</p>;
}


/** Der Dialog selbst — `lauf` beschreibt den Zustand des laufenden
    oder abgeschlossenen Abgleichs. */
function SyncPanel({ lauf, db, onLokal, onServer, onZusammenfuehren, onWiederholen, onClose }) {
  const phase = lauf ? lauf.phase : "laeuft";

  if (phase === "konflikt") {
    return (
      <Konflikt db={db} serverDb={lauf.serverDaten} serverVersion={lauf.serverVersion}
        ergebnis={lauf.basis ? lauf.ergebnis : null}
        onLokal={onLokal} onServer={onServer} onZusammenfuehren={onZusammenfuehren} onClose={onClose} />
    );
  }

  const Fuss = ({ wiederholen = false }) => (
    <div className="wl-modalfuss">
      <div className="wl-modalfuss-rechts">
        {wiederholen && <Btn variante="linie" onClick={onWiederholen}>{t("Erneut versuchen")}</Btn>}
        <Btn variante="voll" onClick={onClose}>{t("Schließen")}</Btn>
      </div>
    </div>
  );

  if (phase === "laeuft") {
    return <p className="wl-fliesstext">{t("Stände werden verglichen …")}</p>;
  }

  if (phase === "offline") {
    return (
      <>
        <p className="wl-fliesstext">
          {t("Der Server ist nicht erreichbar. Die Anwendung arbeitet mit dem lokalen Stand weiter; Änderungen bleiben auf diesem Gerät gesichert und werden beim nächsten erfolgreichen Abgleich übertragen.")}
        </p>
        <Fuss wiederholen />
      </>
    );
  }

  if (phase === "fehler") {
    return (
      <>
        <p className="wl-fliesstext">{lauf.text || t("Der Abgleich ist fehlgeschlagen.")}</p>
        <Fuss wiederholen />
      </>
    );
  }

  const text = {
    aktuell: t("Beide Stände stimmen überein. Es war nichts zu übertragen."),
    hochgeladen: t("Die lokalen Änderungen wurden auf den Server übertragen."),
    heruntergeladen: t("Der Serverstand war neuer und wurde übernommen."),
    zusammengefuehrt: lauf.aufgeloest > 0
      ? t.plural(lauf.aufgeloest,
        lauf.seite === "server"
          ? "Beide Stände wurden zusammengeführt; beim einen Widerspruch gilt der Server."
          : "Beide Stände wurden zusammengeführt; beim einen Widerspruch gilt dieses Gerät.",
        lauf.seite === "server"
          ? "Beide Stände wurden zusammengeführt; bei {n} Widersprüchen gilt der Server."
          : "Beide Stände wurden zusammengeführt; bei {n} Widersprüchen gilt dieses Gerät.")
      : t("Auf beiden Seiten gab es Änderungen. Sie widersprachen sich nicht und wurden zusammengeführt."),
  }[phase] || "";

  /* Etiketten sind Beiwerk des Abgleichs — daher eine Zeile in
     kleiner Schrift, kein eigener Abschnitt. */
  const et = lauf.etiketten;
  const etikettenText = et === "laeuft" || !et ? null
    : [
        et.gesendet > 0
          ? t.plural(et.gesendet, "Ein wartendes Etikett wurde übertragen.", "{n} wartende Etiketten wurden übertragen.")
          : null,
        et.offen > 0
          ? t.plural(et.offen, "Ein Etikett wird im Hintergrund für den Offline-Betrieb geladen.", "{n} Etiketten werden im Hintergrund für den Offline-Betrieb geladen.")
          : null,
      ].filter(Boolean).join(" ");

  return (
    <>
      <p className="wl-fliesstext">{text}</p>
      {lauf.version != null && (
        <p className="wl-statleer">{t("Serverstand {v}", { v: lauf.version })}</p>
      )}
      {phase === "zusammengefuehrt" && (lauf.vonHier > 0 || lauf.vomServer > 0) && (
        <p className="wl-statleer">
          {t("Von diesem Gerät: {a} · vom Server: {b}", { a: lauf.vonHier || 0, b: lauf.vomServer || 0 })}
        </p>
      )}
      {etikettenText && <p className="wl-statleer">{etikettenText}</p>}
      {lauf.katalog && <KatalogZeile bericht={lauf.katalog} />}
      <Fuss />
    </>
  );
}


export { SyncPanel };
